/**
 * MissionSlide — wrapper polimórfico que resolve o componente da missão
 * pelo missionId e injeta contrato comum de slide.
 *
 * O componente concreto (ex.: Reconhecimento) fica em
 * games/<jogo>/<Missao>.tsx e é registrado em games/<jogo>/components.ts.
 * MissionSlide lida apenas com a infra:
 *   - resolve activityId via adapter (sincronizado pelo master)
 *   - dispara setActivity (master) ou logEvent (player) ao clicar em parte
 *     interativa da missão
 */

import { useSessionState } from '../hooks/useSessionState'
import { useAdapter } from '../adapter'
import type { MissionSlide as MissionSlideModel } from '../types/manifest'
import { COMPONENT_REGISTRY } from '../registry/componentRegistry'

interface Props {
  slide: MissionSlideModel
}

export function MissionSlide({ slide }: Props) {
  const adapter = useAdapter()
  const { slideIndex, activityId, interactionMode, membershipId } = useSessionState()
  const isMaster = adapter.role === 'master'
  const isMasterLed = interactionMode === 'master-led'

  // PR-3: resolve atribuição de equipe pro slide atual (vazio em PR-1/PR-2).
  // getMyAssignment retorna null pro master ou se o slide não tem atribuição.
  const assignment = adapter.getMyAssignment(slideIndex)

  const registration = COMPONENT_REGISTRY.resolve(slide.missionId)
  const Component = registration?.component
  if (!Component) {
    return (
      <div style={{ padding: 24, color: 'var(--color-lab-rose, #D4474A)' }}>
        missionId desconhecido: <code>{slide.missionId}</code>
      </div>
    )
  }

  function handleLayerFocused(layer: string) {
    // Convenção: activityId = "{missionId}.{layer}" (mantém namespace limpo).
    const composedActivityId = `${slide.missionId}.${layer}`
    if (isMaster && isMasterLed) {
      // Master só propaga setActivity quando está em modo 'master-led'.
      // Em 'free' ele clica livremente como os alunos (sem enforçar).
      adapter.setActivity(composedActivityId)
    }
    // Telemetria sempre (tanto master quanto player registram foco).
    adapter.logEvent({
      name: `${slide.missionId}.layerFocused`,
      payload: { layer, interactionMode },
    })
  }

  function handleMissionEvent(verb: string, payload: Record<string, unknown>) {
    // Verbos ricos por missão (canal paralelo ao layerFocused legado).
    // Contrato formal: docs/module_lab/EVENTOS.md.
    const enriched = 'interactionMode' in payload
      ? payload
      : { ...payload, interactionMode }
    adapter.logEvent({
      name: `${slide.missionId}.${verb}`,
      payload: enriched,
    })
  }

  // Qual activityId forçar na missão:
  //   master-led → o activityId da sessão (o que o mestre setou)
  //   free       → null em player (aluno controla); master também vê null
  //                e usa seu state local (dentro da missão).
  const forcedActivityId = isMasterLed ? activityId : null

  // readOnly só se aplica ao player em master-led (não altera local; a missão
  // usa o forcedActivityId pra pintar a tela). Em free, todos podem clicar.
  const readOnly = !isMaster && isMasterLed

  return (
    <Component
      currentActivityId={forcedActivityId}
      onLayerFocused={handleLayerFocused}
      onMissionEvent={handleMissionEvent}
      readOnly={readOnly}
      membershipId={membershipId}
      slideConfig={slide as unknown as Record<string, unknown>}
      slideIndex={slideIndex}
      myAssignment={assignment?.payload}
      myTeam={assignment?.teamId}
    />
  )
}
